// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { LeaderboardRecord } from '../../types/leaderboards';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './SolitaireSettings.scss';

const SolitaireSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'config' | 'leaderboard'>('config');
  
  // Config state
  const [configData, setConfigData] = useState<any>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Leaderboard state
  const [leaderboardRecords, setLeaderboardRecords] = useState<LeaderboardRecord[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();
  
  const SOLITAIRE_LEADERBOARD_ID = 'total_earned_coins_solitaire';

  useEffect(() => {
    loadSolitaireConfig();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboardData();
    }
  }, [activeTab]);

  const loadSolitaireConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to load from backend RPC
      const response = await consoleService.callRpcFunction({
        id: 'solitaire_game_config_get',
        body: JSON.stringify({}),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });
      
      if (response.body) {
        const config = JSON.parse(response.body);
        setConfigData(config);
      } else {
        // Use default config if not found
        setConfigData(getDefaultConfig());
      }
    } catch (err) {
      console.warn('Failed to load solitaire config from backend, using defaults:', err);
      setConfigData(getDefaultConfig());
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboardData = async () => {
    try {
      setLeaderboardLoading(true);
      setError(null);
      
      const data = await consoleService.getSolitaireLeaderboard(SOLITAIRE_LEADERBOARD_ID);
      setLeaderboardRecords(data.records || []);
    } catch (err: any) {
      console.error('Failed to load leaderboard:', err);
      setLeaderboardRecords([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const getDefaultConfig = () => ({
    reward_config: {
      move_stock_to_tableau: 5,
      move_stock_to_foundation: 10,
      move_tableau_to_foundation: 10,
      move_foundation_to_tableau: -10,
      flip_tableau_card: 5,
      recycle_stock: 0,
      complete_foundation_pile: 50,
      winning_game: 200,
      time_bonus_points_threshold_seconds: 300,
      time_bonus_points: 100
    },
    lifeline_costs: {
      hint: 5,
      undo: 8,
      auto_move: 15
    },
    draw_count: 1,
    challenge_mode_time_minutes: 10,
    winner_reward: {
      currency: 4,
      amount: 100
    },
    time_bonus_coins_threshold_seconds: 300,
    time_bonus_coins: 75
  });

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await consoleService.callRpcFunction({
        id: 'solitaire_game_config_resetup',
        body: JSON.stringify(configData),
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });
      
      setHasUnsavedChanges(false);
      setSuccessMessage('Configuration saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field: string, value: any) => {
    setConfigData((prev: any) => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleNestedConfigChange = (parent: string, field: string, value: any) => {
    setConfigData((prev: any) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleDeleteRecord = (record: LeaderboardRecord, index: number) => {
    openConfirmDialog(
      'Remove Record',
      `Are you sure you want to remove the record for "${record.username || record.owner_id}"?`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', SOLITAIRE_LEADERBOARD_ID, record.owner_id);
          setLeaderboardRecords(prev => prev.filter((_, i) => i !== index));
          setSuccessMessage('Record removed successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to remove record');
        }
      }
    );
  };

  const handleResetLeaderboard = () => {
    openConfirmDialog(
      'Reset Leaderboard',
      'Are you sure you want to reset the Solitaire leaderboard? This will delete all records.',
      async () => {
        setResetting(true);
        try {
          await consoleService.leaderboardReset(SOLITAIRE_LEADERBOARD_ID);
          await loadLeaderboardData();
          setSuccessMessage('Leaderboard reset successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to reset leaderboard');
        } finally {
          setResetting(false);
        }
      }
    );
  };

  const formatScore = (score: string): string => {
    return parseInt(score).toLocaleString();
  };

  const getRankIcon = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !configData) {
    return (
      <div className="solitaire-settings">
        <div className="loading-spinner">Loading Solitaire settings...</div>
      </div>
    );
  }

  return (
    <div className="solitaire-settings">
      <div className="solitaire-settings-header">
        <h1>🃏 Solitaire Game Settings</h1>
        <p>Configure Solitaire game settings and view leaderboard</p>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          ⚙️ Configuration
        </button>
        <button 
          className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          🏆 Leaderboard
        </button>
      </div>

      <div className="solitaire-settings-content">
        {/* Configuration Tab */}
        {activeTab === 'config' && configData && (
          <div className="config-tab">
            {hasUnsavedChanges && (
              <div className="unsaved-banner">
                <span>⚠️ You have unsaved changes</span>
                <button onClick={handleSaveConfig} className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            <div className="config-section">
              <h3>🎴 Game Settings</h3>
              <div className="config-grid">
                <div className="config-field">
                  <label>Draw Count</label>
                  <input 
                    type="number" 
                    value={configData.draw_count || 1}
                    onChange={(e) => handleConfigChange('draw_count', parseInt(e.target.value) || 1)}
                    min="1"
                    max="3"
                  />
                </div>
                <div className="config-field">
                  <label>Challenge Mode Time (minutes)</label>
                  <input 
                    type="number" 
                    value={configData.challenge_mode_time_minutes || 10}
                    onChange={(e) => handleConfigChange('challenge_mode_time_minutes', parseInt(e.target.value) || 10)}
                    min="1"
                  />
                </div>
              </div>
            </div>

            <div className="config-section">
              <h3>🏆 Winner Reward</h3>
              <div className="config-grid">
                <div className="config-field">
                  <label>Currency Type</label>
                  <select 
                    value={configData.winner_reward?.currency || 4}
                    onChange={(e) => handleNestedConfigChange('winner_reward', 'currency', parseInt(e.target.value))}
                  >
                    <option value={1}>Coin</option>
                    <option value={2}>Ludo Coin</option>
                    <option value={3}>Quiz Coin</option>
                    <option value={4}>Solitaire Coin</option>
                  </select>
                </div>
                <div className="config-field">
                  <label>Reward Amount</label>
                  <input 
                    type="number" 
                    value={configData.winner_reward?.amount || 100}
                    onChange={(e) => handleNestedConfigChange('winner_reward', 'amount', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="config-section">
              <h3>⏱️ Time Bonus</h3>
              <div className="config-grid">
                <div className="config-field">
                  <label>Time Bonus Points Threshold (seconds)</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.time_bonus_points_threshold_seconds || 300}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'time_bonus_points_threshold_seconds', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="config-field">
                  <label>Time Bonus Points</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.time_bonus_points || 100}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'time_bonus_points', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="config-field">
                  <label>Time Bonus Coins Threshold (seconds)</label>
                  <input 
                    type="number" 
                    value={configData.time_bonus_coins_threshold_seconds || 300}
                    onChange={(e) => handleConfigChange('time_bonus_coins_threshold_seconds', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="config-field">
                  <label>Time Bonus Coins</label>
                  <input 
                    type="number" 
                    value={configData.time_bonus_coins || 75}
                    onChange={(e) => handleConfigChange('time_bonus_coins', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="config-section">
              <h3>💎 Lifeline Costs</h3>
              <div className="config-grid">
                <div className="config-field">
                  <label>Hint Cost</label>
                  <input 
                    type="number" 
                    value={configData.lifeline_costs?.hint || 5}
                    onChange={(e) => handleNestedConfigChange('lifeline_costs', 'hint', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="config-field">
                  <label>Undo Cost</label>
                  <input 
                    type="number" 
                    value={configData.lifeline_costs?.undo || 8}
                    onChange={(e) => handleNestedConfigChange('lifeline_costs', 'undo', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="config-field">
                  <label>Auto Move Cost</label>
                  <input 
                    type="number" 
                    value={configData.lifeline_costs?.auto_move || 15}
                    onChange={(e) => handleNestedConfigChange('lifeline_costs', 'auto_move', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="config-section">
              <h3>📊 Reward Config (Points)</h3>
              <div className="config-grid">
                <div className="config-field">
                  <label>Move Stock to Tableau</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.move_stock_to_tableau || 5}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'move_stock_to_tableau', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="config-field">
                  <label>Move Stock to Foundation</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.move_stock_to_foundation || 10}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'move_stock_to_foundation', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="config-field">
                  <label>Move Tableau to Foundation</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.move_tableau_to_foundation || 10}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'move_tableau_to_foundation', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="config-field">
                  <label>Move Foundation to Tableau</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.move_foundation_to_tableau || -10}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'move_foundation_to_tableau', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="config-field">
                  <label>Flip Tableau Card</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.flip_tableau_card || 5}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'flip_tableau_card', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="config-field">
                  <label>Recycle Stock</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.recycle_stock || 0}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'recycle_stock', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="config-field">
                  <label>Complete Foundation Pile</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.complete_foundation_pile || 50}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'complete_foundation_pile', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
                <div className="config-field">
                  <label>Winning Game</label>
                  <input 
                    type="number" 
                    value={configData.reward_config?.winning_game || 200}
                    onChange={(e) => handleNestedConfigChange('reward_config', 'winning_game', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="leaderboard-tab">
            <div className="leaderboard-header">
              <h3>🏆 Solitaire Leaderboard</h3>
              <div className="leaderboard-actions">
                <button
                  className="btn btn-danger"
                  onClick={handleResetLeaderboard}
                  disabled={resetting}
                >
                  {resetting ? 'Resetting...' : '🔄 Reset Leaderboard'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={loadLeaderboardData}
                  disabled={leaderboardLoading}
                >
                  {leaderboardLoading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {leaderboardLoading ? (
              <div className="loading-spinner">Loading leaderboard...</div>
            ) : leaderboardRecords.length > 0 ? (
              <div className="leaderboard-table">
                <div className="table-header">
                  <div className="header-cell rank-cell">Rank</div>
                  <div className="header-cell player-cell">Player</div>
                  <div className="header-cell score-cell">Score</div>
                  <div className="header-cell date-cell">Last Updated</div>
                  <div className="header-cell actions-cell">Actions</div>
                </div>
                <div className="table-body">
                  {leaderboardRecords.map((record, index) => {
                    const rank = record.rank || (index + 1);
                    return (
                      <div key={record.owner_id} className={`table-row ${rank <= 3 ? 'top-rank' : ''}`}>
                        <div className="table-cell rank-cell">
                          <span className="rank-icon">{getRankIcon(rank)}</span>
                          <span className="rank-number">#{rank}</span>
                        </div>
                        <div className="table-cell player-cell">
                          <div className="player-info">
                            <div className="player-name">{record.username || 'Anonymous'}</div>
                            <div className="player-id">ID: {record.owner_id.substring(0, 8)}...</div>
                          </div>
                        </div>
                        <div className="table-cell score-cell">
                          <div className="score-value">{formatScore(record.score)}</div>
                        </div>
                        <div className="table-cell date-cell">
                          <div className="update-time">{formatDate(record.update_time)}</div>
                        </div>
                        <div className="table-cell actions-cell">
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRecord(record, index)}
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-leaderboard">
                <div className="empty-icon">🏆</div>
                <h3>No Rankings Yet</h3>
                <p>No players have earned coins in Solitaire yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default SolitaireSettings;
